import { z } from 'zod';

/**
 * Revenue by year data structure used in rules engine
 */
export const QuoteRevenueByYearSpanSchema = z.object({
  start: z.string(),
  end: z.string(),
  lengthInDays: z.number(),
});

export type QuoteRevenueByYearSpan = z.infer<
  typeof QuoteRevenueByYearSpanSchema
>;

export const QuoteRevenueByYearRevenueSchema = z.object({
  current: z.object({
    arr: z.number(),
    mrr: z.number(),
  }),
  previous: z
    .object({
      arr: z.number(),
      mrr: z.number(),
    })
    .nullable(),
  delta: z.object({
    arr: z.number(),
    mrr: z.number(),
  }),
});

export type QuoteRevenueByYearRevenue = z.infer<
  typeof QuoteRevenueByYearRevenueSchema
>;

export const QuoteRevenueByYearSchema = z.object({
  start: z.string(),
  end: z.string(),
  revenue: QuoteRevenueByYearRevenueSchema,
  span: QuoteRevenueByYearSpanSchema,
});

export type QuoteRevenueByYear = z.infer<typeof QuoteRevenueByYearSchema>;

/**
 * Per-year breakdown returned by
 * `GET /api/quotes/{quoteId}/revenueMetrics?includeYearlyBreakdown=true`.
 *
 * Years are 12-month spans anchored on the quote start date (not calendar
 * years). Each year repeats the offerings/items that carry an amount in that
 * year, so a product only present in year 1 is simply absent from year 2 —
 * consumers building a table have to union the items across years.
 */
export const QuoteYearlyBreakdownItemSchema = z.object({
  quoteItemId: z.string(),
  productName: z.string(),
  productType: z.string(),
  productTypeLabel: z.string().nullish(),
  /**
   * The product behind the item. A ramp repeats its parent's products under
   * new quote item ids, so this is what pairs the two sides up when their
   * amounts are merged into one row. Nullish because it postdates the rest of
   * this payload.
   */
  productId: z.string().nullish(),
  amount: z.number(),
  position: z.number(),
});

export type QuoteYearlyBreakdownItem = z.infer<
  typeof QuoteYearlyBreakdownItemSchema
>;

export const QuoteYearlyBreakdownOfferingSchema = z.object({
  quoteOfferingId: z.string(),
  offeringName: z.string(),
  position: z.number(),
  items: z.array(QuoteYearlyBreakdownItemSchema),
  /**
   * A scheduled change ("ramp") arrives as its own offering alongside the one
   * it ramps, sharing that offering's name and `position`. Nullish because the
   * field postdates the rest of this payload.
   */
  isRamp: z.boolean().nullish(),
});

export type QuoteYearlyBreakdownOffering = z.infer<
  typeof QuoteYearlyBreakdownOfferingSchema
>;

export const QuoteYearlyBreakdownAmountsSchema = z.object({
  total: z.number(),
  recurring: z.number(),
  oneTime: z.number(),
  oneTimeDiscount: z.number(),
});

export type QuoteYearlyBreakdownAmounts = z.infer<
  typeof QuoteYearlyBreakdownAmountsSchema
>;

export const QuoteYearlyBreakdownSchema = z.object({
  year: z.number(),
  startDate: z.string(),
  endDate: z.string(),
  amounts: QuoteYearlyBreakdownAmountsSchema,
  offerings: z.array(QuoteYearlyBreakdownOfferingSchema),
});

export type QuoteYearlyBreakdown = z.infer<typeof QuoteYearlyBreakdownSchema>;

export type QuoteRevenueMetrics = {
  revenueByYear: QuoteRevenueByYear[];
  /** Only populated when the request passes `includeYearlyBreakdown=true`. */
  yearlyBreakdown?: QuoteYearlyBreakdown[];
};
